import { Loader2, RefreshCw, Save, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { COST_NOTE, useRagSettings } from "./rag/useRagSettings";
import { ReembedConfirmDialog } from "./rag/ReembedConfirmDialog";

export const RagSettingsTab = () => {
  const s = useRagSettings();

  if (s.loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading RAG settings…
      </div>
    );
  }

  const referenceChunkTokens = s.settings?.chunk_size_tokens ?? s.size;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Global RAG settings</CardTitle>
          <CardDescription>
            These values apply to every workspace's Corpus. Changing them only affects newly embedded
            content — use the Re-RAG controls below to refresh existing chunks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rag-size">Chunk size (tokens)</Label>
              <Input
                id="rag-size" type="number" min={200} max={2000}
                value={s.size}
                onChange={(e) => s.setSize(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">200–2000. Default 800. (~4 chars per token.)</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rag-overlap">Overlap (tokens)</Label>
              <Input
                id="rag-overlap" type="number" min={0} max={500}
                value={s.overlap}
                onChange={(e) => s.setOverlap(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">0–500. Must be less than chunk size. Default 150.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Splitting strategy</Label>
              <Select value="recursive" disabled>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recursive">Recursive Structural</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Locked. Other strategies will be added later.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Embedding model</Label>
              <Select value="text-embedding-3-small" disabled>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text-embedding-3-small">text-embedding-3-small (1536d)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Locked pre-launch. Changing dimensions requires reindexing the vector column.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="text-xs text-muted-foreground">
              Last updated {s.settings ? new Date(s.settings.updated_at).toLocaleString() : "—"}
            </div>
            <Button onClick={s.handleSave} disabled={s.saving}>
              {s.saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save settings
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Re-RAG (re-embed Corpus docs)</CardTitle>
          <CardDescription>
            Re-runs chunking + embedding using the current global settings. Existing chunks for each
            document are wiped and replaced. {COST_NOTE} A typical 5K-token doc costs roughly $0.0001.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Re-embed one workspace</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={s.workspaceId} onValueChange={s.setWorkspaceId}>
                <SelectTrigger className="w-[320px]">
                  <SelectValue placeholder={s.workspaces.length === 0 ? "No workspaces with Corpus docs" : "Pick a workspace"} />
                </SelectTrigger>
                <SelectContent>
                  {s.workspaces.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                disabled={!s.workspaceId || s.reembedSingleBusy}
                onClick={() => void s.openWsDialog()}
              >
                {s.reembedSingleBusy
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <RefreshCw className="mr-2 h-4 w-4" />}
                Re-embed all corpus docs
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{COST_NOTE}</p>
          </div>

          <div className="space-y-2">
            <Label>Re-embed every workspace on the platform</Label>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This re-embeds every Corpus doc in every workspace. It can use a meaningful amount of
                embedding credits and may take several minutes.
              </AlertDescription>
            </Alert>
            <Button variant="destructive" onClick={() => void s.openAllDialog()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Re-RAG every workspace
            </Button>
            <p className="text-xs text-muted-foreground">{COST_NOTE}</p>
          </div>
        </CardContent>
      </Card>

      <ReembedConfirmDialog
        open={s.confirmWsOpen}
        onOpenChange={s.setConfirmWsOpen}
        title="Re-embed this workspace?"
        description="Wipes and regenerates every Corpus chunk in the selected workspace."
        confirmPhrase="RE-RAG"
        confirmText={s.confirmWsText}
        onConfirmTextChange={s.setConfirmWsText}
        estimate={s.wsEstimate}
        referenceChunkTokens={referenceChunkTokens}
        busy={s.reembedSingleBusy}
        onConfirm={() => void s.handleReembedWorkspace()}
        actionLabel="Re-embed workspace"
      />

      <ReembedConfirmDialog
        open={s.confirmAllOpen}
        onOpenChange={s.setConfirmAllOpen}
        title="Re-embed every workspace?"
        description="Wipes and regenerates every Corpus chunk on the entire platform."
        confirmPhrase="RE-RAG ALL"
        confirmText={s.confirmAllText}
        onConfirmTextChange={s.setConfirmAllText}
        estimate={s.allEstimate}
        referenceChunkTokens={referenceChunkTokens}
        busy={s.reembedAllBusy}
        onConfirm={() => void s.handleReembedAll()}
        actionLabel="Re-embed everything"
        showWorkspacesLine
      />
    </div>
  );
};

export default RagSettingsTab;
